import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/shared/ui/toast';
import { BUILD_ID, shortBuildId } from '@/shared/lib/app-version';
import { clearPendingUpdate, readPendingUpdate, resolveUpdateOutcome } from './update-flow';

/**
 * Confirms, once per reload, that an accepted update actually took effect.
 *
 * A reload on its own is ambiguous: the page comes back looking identical,
 * so without this the user cannot tell whether "Update" did anything. The
 * previous bundle left its build id in `sessionStorage` before reloading, so
 * this compares that record against the build now running.
 *
 * `sessionStorage` is read in an effect rather than during render because it
 * is a browser global: reading it at module scope or during render would make
 * the first paint depend on storage that the server render or a hydration pass
 * cannot see.
 */
export function UpdateNotice() {
  const { t } = useTranslation();
  const toast = useToast();
  /** Guards against a double report under React StrictMode's double effect. */
  const reported = useRef(false);

  useEffect(() => {
    if (reported.current) return;
    reported.current = true;

    const outcome = resolveUpdateOutcome(readPendingUpdate(), BUILD_ID);
    if (outcome === null) return;

    clearPendingUpdate();

    if (outcome.kind === 'updated') {
      toast.success(
        t('pwa.updatedTitle'),
        t('pwa.updatedBody', { version: shortBuildId(outcome.to) }),
      );
      return;
    }

    // The reload came back on the same build. Saying "updated" here would be
    // a lie the user could catch by looking at the version in Settings, so the
    // notice asks them to try once more instead.
    toast.warning(t('pwa.updateStalledTitle'), t('pwa.updateStalledBody'));
  }, [t, toast]);

  return null;
}
